import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { useRouter } from '@tanstack/react-router'

export const Route = createFileRoute('/share-target')({
  component: ShareTargetPage,
})

/**
 * Share Target Handler
 * 
 * This page is the target for the Web Share Target API.
 * When users share a PDF or image to GearLens from another app:
 * 1. The browser POSTs the file to this route
 * 2. We extract and store the file in IndexedDB
 * 3. Redirect to /upload where the file will be auto-loaded
 * 
 * The POST data is accessible via the page's request on initial load.
 */
function ShareTargetPage() {
  const router = useRouter()
  const [status, setStatus] = useState<'processing' | 'error' | 'redirecting'>('processing')
  const [errorMessage, setErrorMessage] = useState<string>('')

  useEffect(() => {
    const handleSharedFiles = async () => {
      try {
        // Check if we have files in the form data
        // The Web Share Target API POSTs files as multipart/form-data
        // We need to check the document's FormData if available
        
        // For PWA share targets, the files are available via the page's context
        // Try to access shared files from various sources
        
        const urlParams = new URLSearchParams(window.location.search)
        const hasFileParam = urlParams.has('file')
        
        if (hasFileParam || window.location.search.includes('file')) {
          // Files were shared - store them in IndexedDB
          await storeSharedFiles()
          setStatus('redirecting')
          
          // Redirect to upload page with a flag
          setTimeout(() => {
            router.navigate({ to: '/upload', search: { shared: 'true' } })
          }, 500)
        } else {
          // Check if we're being loaded as the target of a POST
          // In which case, store a flag and reload as GET
          if (sessionStorage.getItem('share-target-post') !== 'true') {
            sessionStorage.setItem('share-target-post', 'true')
            setStatus('processing')
            // Give the browser time to process the POST
            setTimeout(() => {
              window.location.href = '/share-target'
            }, 100)
          } else {
            // Clean up and redirect to upload
            sessionStorage.removeItem('share-target-post')
            setStatus('redirecting')
            setTimeout(() => {
              router.navigate({ to: '/upload' })
            }, 500)
          }
        }
      } catch (error) {
        console.error('Error handling shared files:', error)
        setStatus('error')
        setErrorMessage(error instanceof Error ? error.message : 'Unknown error')
        
        // Redirect to upload anyway after showing error
        setTimeout(() => {
          router.navigate({ to: '/upload' })
        }, 2000)
      }
    }

    handleSharedFiles()
  }, [router])

  return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800">
      <div className="text-center p-8">
        {status === 'processing' && (
          <>
            <div className="inline-block animate-spin rounded-full h-16 w-16 border-b-4 border-sky-500 mb-6"></div>
            <p className="text-xl font-semibold text-slate-700 dark:text-slate-300">
              Processing shared file...
            </p>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-2">
              Please wait while we prepare your file for upload
            </p>
          </>
        )}
        
        {status === 'redirecting' && (
          <>
            <div className="inline-block rounded-full h-16 w-16 bg-green-500 flex items-center justify-center mb-6">
              <svg className="w-10 h-10 text-white" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                <path d="M5 13l4 4L19 7"></path>
              </svg>
            </div>
            <p className="text-xl font-semibold text-slate-700 dark:text-slate-300">
              File ready!
            </p>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-2">
              Redirecting to upload page...
            </p>
          </>
        )}
        
        {status === 'error' && (
          <>
            <div className="inline-block rounded-full h-16 w-16 bg-red-500 flex items-center justify-center mb-6">
              <svg className="w-10 h-10 text-white" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                <path d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </div>
            <p className="text-xl font-semibold text-red-600 dark:text-red-400">
              Error processing file
            </p>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-2">
              {errorMessage || 'An error occurred'}
            </p>
            <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
              Redirecting to upload page...
            </p>
          </>
        )}
      </div>
    </div>
  )
}

async function storeSharedFiles() {
  // Store a flag in sessionStorage that indicates files were shared
  // The actual file handling will be done by the upload page
  sessionStorage.setItem('pwa-shared-files', 'true')
  sessionStorage.setItem('pwa-shared-timestamp', Date.now().toString())
}

